import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { trackAIReferral } from './lib/aiAnalytics';
// La home se carga sin lazy: es el LCP. El resto, por código separado.
import HomePage from './pages/HomePage';
import './App.css';

// Lazy load secondary pages for code splitting
const DemoPage = lazy(() => import('./pages/DemoPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const BlogCategoryPage = lazy(() => import('./pages/BlogCategoryPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const LegalNoticePage = lazy(() => import('./pages/LegalNoticePage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const InformePage = lazy(() => import('./pages/InformePage'));
const RenovablesPage = lazy(() => import('./pages/RenovablesPage'));
const UrbanismoPage = lazy(() => import('./pages/UrbanismoPage'));
const TerritorioPage = lazy(() => import('./pages/TerritorioPage'));
const MetodologiaPage = lazy(() => import('./pages/MetodologiaPage'));
const AccesoUrbanismoPage = lazy(() => import('./pages/AccesoUrbanismoPage'));
const GlosarioPage = lazy(() => import('./pages/GlosarioPage'));
const LocalePage = lazy(() => import('./pages/LocalePage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Scroll to top on route change (o al ancla, p. ej. /territorio#precios) + AI referral tracking
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // La página puede ser lazy: se reintenta hasta que el ancla exista.
    let intentos = 0;
    let t;
    const ir = () => {
      const el = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (el) el.scrollIntoView();
      else if (intentos++ < 20) t = setTimeout(ir, 100);
    };
    ir();
    return () => clearTimeout(t);
  }, [pathname, hash]);

  // Track AI referrals (ChatGPT, Perplexity, Claude, etc.) - una vez por sesión
  useEffect(() => {
    const sessionKey = 'geovis_ai_tracked';
    if (!sessionStorage.getItem(sessionKey)) {
      trackAIReferral();
      sessionStorage.setItem(sessionKey, 'true');
    }
  }, []);

  return null;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/demo" element={<DemoPage />} />
          <Route path="/informe" element={<InformePage />} />
          <Route path="/renovables" element={<RenovablesPage />} />
          <Route path="/urbanismo" element={<UrbanismoPage />} />
          <Route path="/urbanismo/acceso" element={<AccesoUrbanismoPage />} />
          <Route path="/urbanismo/metodologia" element={<MetodologiaPage />} />
          <Route path="/urbanismo/glosario" element={<GlosarioPage />} />
          <Route path="/urbanismo/glosario/:termino" element={<GlosarioPage />} />
          <Route path="/territorio" element={<TerritorioPage />} />
          {/* Locales — react-router v7 no soporta regex; LocalePage valida y redirige a /404 si no es válido */}
          <Route path="/cl" element={<LocalePage />} />
          <Route path="/pe" element={<LocalePage />} />
          <Route path="/pt" element={<LocalePage />} />
          <Route path="/it" element={<LocalePage />} />
          <Route path="/fr" element={<LocalePage />} />
          <Route path="/ro" element={<LocalePage />} />
          <Route path="/no" element={<LocalePage />} />
          <Route path="/pl" element={<LocalePage />} />
          <Route path="/cz" element={<LocalePage />} />
          <Route path="/uk" element={<LocalePage />} />
          <Route path="/mx" element={<LocalePage />} />
          <Route path="/co" element={<LocalePage />} />
          <Route path="/ec" element={<LocalePage />} />
          <Route path="/za" element={<LocalePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/categoria/:category" element={<BlogCategoryPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/aviso-legal" element={<LegalNoticePage />} />
          <Route path="/privacidad" element={<PrivacyPage />} />
          <Route path="/terminos" element={<TermsPage />} />
          {/* Catch-all: rutas desconocidas (incluye el /404 al que redirige LocalePage) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
